<template>
  <div
    class="w-full text-gray-900 border border-gray-300 rounded-lg p-2.5 text-sm flex items-center justify-between"
    :class="{
      'cursor-pointer': !disabled,
      'cursor-not-allowed opacity-60': disabled,
      'ring-2 ring-blue-500 border-blue-500': open,
      'focus:ring-blue-500 focus:border-blue-500': !open && !disabled,
      'dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500': true,
      'bg-gray-50': !inline,
      'bg-white': inline && !validation,
      'bg-red-50 border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 focus:border-red-500 dark:text-red-500 dark:placeholder-red-500 dark:border-red-500':
        validation
    }"
    :tabindex="disabled ? -1 : 0"
    :aria-disabled="disabled || undefined"
    @click="emit('toggle')"
  >
    <div v-if="multiselect && chips.length > 0" class="flex flex-wrap gap-1">
      <span
        v-for="chip in chips"
        :key="chip.value"
        class="inline-flex items-center px-2 py-0.5 bg-gray-200 rounded dark:bg-gray-600"
      >
        <span class="text-xs font-medium text-gray-600 dark:text-gray-300">{{
          chip.label
        }}</span>
        <button
          v-if="!disabled"
          type="button"
          class="inline-flex pl-1 pt-0.5 items-center ms-1 text-sm bg-transparent rounded-xs"
          aria-label="Remove"
          @click.stop="emit('remove', chip.value)"
        >
          <svg
            class="w-2 h-2"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 14 14"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
            />
          </svg>
          <span class="sr-only">Remove badge</span>
        </button>
      </span>
    </div>

    <span v-else-if="!multiselect && selectedOption">
      <slot name="selected" :option="selectedOption">
        {{ selectedOption.label }}
      </slot>
    </span>
    <span v-else class="text-gray-500 dark:text-gray-400">{{
      placeholder
    }}</span>
    <div class="cursor-pointer relative z-10 pointer-events-none">
      <svg
        v-if="!open"
        class="w-4 h-4 ml-2"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M19 9l-7 7-7-7"
        ></path>
      </svg>

      <svg
        v-if="open"
        class="w-4 h-4 ml-2"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M5 15l7-7 7 7"
        ></path>
      </svg>
    </div>
  </div>
</template>

<script lang="ts">
/** One selected value shown as a removable chip. */
export interface SelectInputChip {
  value: string | number;
  label: string;
}
</script>

<script setup lang="ts">
import type { SelectInputOption } from "./SelectInputOptions.vue";

// The clickable box of a select: the current selection as chips (multi) or a
// label (single), the placeholder when there is none, and a chevron that
// follows the open state. Knows nothing about where the options are shown.
withDefaults(
  defineProps<{
    chips?: SelectInputChip[];
    selectedOption?: SelectInputOption | null;
    multiselect?: boolean;
    placeholder?: string;
    open?: boolean;
    disabled?: boolean;
    inline?: boolean;
    validation?: string;
  }>(),
  {
    chips: () => [],
    selectedOption: null,
    multiselect: false,
    placeholder: "",
    open: false,
    disabled: false,
    inline: false,
    validation: ""
  }
);

const emit = defineEmits<{
  (e: "toggle"): void;
  (e: "remove", value: string | number): void;
}>();
</script>
